const WIFI_SAVED_AT_STORAGE_PREFIX = "installation_wifi_saved_at_";

function getWifiSavedAtStorageKey(cameraId) {
  return `${WIFI_SAVED_AT_STORAGE_PREFIX}${cameraId}`;
}

export function parseWifiCredentialsFromConfig(configData) {
  const raw = configData?.wifi_credentials;
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .filter((entry) => entry && typeof entry === "object")
    .map((entry) => ({
      ssid: String(entry.ssid || "").trim(),
      password: String(entry.password || ""),
    }))
    .filter((entry) => entry.ssid.length > 0);
}

export function getPrimaryWifiCredential(configData) {
  const credentials = parseWifiCredentialsFromConfig(configData);
  return credentials[0] ?? null;
}

export function validateWifiCredentialDraft({ ssid, password }) {
  const trimmedSsid = String(ssid || "").trim();
  const trimmedPassword = String(password || "");

  if (!trimmedSsid) {
    return { valid: false, errorKey: "installation_wifi_ssid_required" };
  }

  if (trimmedSsid.length > 32) {
    return { valid: false, errorKey: "installation_wifi_ssid_too_long" };
  }

  if (!trimmedPassword) {
    return { valid: false, errorKey: "installation_wifi_password_required" };
  }

  if (trimmedPassword.length < 8) {
    return { valid: false, errorKey: "installation_wifi_password_too_short" };
  }

  return { valid: true };
}

export function buildWifiCredentialsAzurePartial(ssid, password) {
  return {
    wifi_credentials: [
      {
        ssid: String(ssid).trim(),
        password: String(password),
      },
    ],
  };
}

export function buildWifiCredentialsClearPartial() {
  return {
    wifi_credentials: [],
  };
}

export function hasWifiCredentialsInConfig(configData) {
  return parseWifiCredentialsFromConfig(configData).length > 0;
}

export function areWifiDraftsEqual(left, right) {
  return (
    String(left?.ssid || "").trim() === String(right?.ssid || "").trim() &&
    String(left?.password || "") === String(right?.password || "")
  );
}

export function readWifiSavedAt(cameraId) {
  const raw = localStorage.getItem(getWifiSavedAtStorageKey(cameraId));
  if (!raw) {
    return null;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

export function writeWifiSavedAt(cameraId, date = new Date()) {
  localStorage.setItem(getWifiSavedAtStorageKey(cameraId), date.toISOString());
}

export function clearWifiSavedAt(cameraId) {
  localStorage.removeItem(getWifiSavedAtStorageKey(cameraId));
}

export function formatWifiSavedAt(t, date, language) {
  if (!date) {
    return "";
  }

  const locale = language?.startsWith("fr") ? "fr-FR" : "en-GB";
  const datePart = date.toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });

  return t("installation_wifi_saved_at", { date: datePart, time: timePart });
}

export function hasExistingSsidInConfig(configData, ssid) {
  const trimmedSsid = String(ssid || "").trim();
  if (!trimmedSsid) {
    return false;
  }

  return parseWifiCredentialsFromConfig(configData).some(
    (entry) => entry.ssid === trimmedSsid,
  );
}
